import { useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { useMyCosmetics, useSaveAppearance } from '@/api/hooks'
import type { AvatarConfigDto, BlasonConfigDto, CosmeticItemDto } from '@/api/hooks'
import { CaptainPortrait } from '@/components/game/cosmetics/CaptainPortrait'
import { CaptainBlason } from '@/components/game/cosmetics/CaptainBlason'
import { useAuth } from '@/auth/useAuth'
import { useMediaQuery } from '@/hooks/useMediaQuery'

/** Onglets de la cabine : slots avatar, puis blason, puis titre. */
const TABS: { slot: string; label: string; group: 'avatar' | 'blason' | 'title' }[] = [
  { slot: 'face', label: 'Visage', group: 'avatar' },
  { slot: 'hair', label: 'Coiffure', group: 'avatar' },
  { slot: 'facialHair', label: 'Pilosité', group: 'avatar' },
  { slot: 'headgear', label: 'Couvre-chef', group: 'avatar' },
  { slot: 'accessory', label: 'Accessoire', group: 'avatar' },
  { slot: 'background', label: 'Fond', group: 'avatar' },
  { slot: 'frame', label: 'Cadre', group: 'avatar' },
  { slot: 'shape', label: 'Forme du blason', group: 'blason' },
  { slot: 'glyph', label: 'Emblème', group: 'blason' },
  { slot: 'palette', label: 'Couleurs', group: 'blason' },
  { slot: 'field', label: 'Champ', group: 'blason' },
  { slot: 'title', label: 'Titre', group: 'title' },
]

/** Regroupement visuel des onglets — Avatar / Blason / Titre. */
const TAB_GROUPS: { id: 'avatar' | 'blason' | 'title'; label: string }[] = [
  { id: 'avatar', label: 'Avatar' },
  { id: 'blason', label: 'Blason' },
  { id: 'title', label: 'Titre' },
]

/** Config avatar par défaut proposée à la première visite (items de base). */
const DEFAULT_AVATAR: AvatarConfigDto = { face: 'face_teint_hale', background: 'bg_ocean_profond' }
const DEFAULT_BLASON: BlasonConfigDto = { shape: 'shape_ecu_pointe', glyph: 'glyph_ancre', palette: 'pal_ocean_or' }

/**
 * Complète les slots avatar optionnels laissés vides (headgear/accessory/frame)
 * avec leur item « Aucun » du catalogue : un config frais les laisse à
 * undefined alors que « Aucun » est bien l'état effectif — sans ce remplissage,
 * aucune tuile n'apparaît sélectionnée dans ces onglets. Pure ; appelée à
 * l'hydratation uniquement, et ne marque PAS le brouillon comme modifié.
 */
export function fillEmptyAvatarSlots(avatar: AvatarConfigDto, catalog: CosmeticItemDto[]): AvatarConfigDto {
  const noneIfInCatalog = (key: string) => (catalog.some((i) => i.key === key) ? key : null)
  return {
    ...avatar,
    headgear: avatar.headgear ?? noneIfInCatalog('hat_none'),
    accessory: avatar.accessory ?? noneIfInCatalog('acc_none'),
    frame: avatar.frame ?? noneIfInCatalog('frame_none'),
  }
}

/**
 * /capitaine/cabine — la cabine du capitaine : composition du portrait peint,
 * du blason et du titre équipé. Aperçu vivant à gauche (sticky sur desktop,
 * bandeau compact sticky sur mobile), catalogue à onglets groupés à droite.
 * Chaque tuile de la grille montre une vignette « et si ? » : la config
 * courante avec CET item appliqué (les items *_none rendent la config sans
 * l'élément). Les items non possédés restent visibles mais verrouillés, avec
 * leur condition de déblocage — c'est la carotte.
 */
export function Cabine() {
  const { user } = useAuth()
  const { data, isLoading } = useMyCosmetics()
  const save = useSaveAppearance()
  // Bandeau mobile : gate JS (pas seulement CSS) pour ne pas dupliquer le
  // bouton Sauvegarder dans l'arbre d'accessibilité sur desktop.
  const isMobile = useMediaQuery('(max-width: 1023px)', false)

  const [tab, setTab] = useState('face')
  const [avatar, setAvatar] = useState<AvatarConfigDto | null>(null)
  const [blason, setBlason] = useState<BlasonConfigDto | null>(null)
  const [title, setTitle] = useState<string | null>(null)
  const [dirty, setDirty] = useState(false)
  const [hydrated, setHydrated] = useState(false)

  // Hydratation du brouillon local depuis le serveur, une seule fois
  // (render-phase update : React relance le rendu avant de committer).
  if (data && !hydrated) {
    setAvatar(fillEmptyAvatarSlots(data.avatarConfig ?? DEFAULT_AVATAR, data.catalog))
    setBlason(data.blasonConfig ?? DEFAULT_BLASON)
    setTitle(data.equippedTitle)
    setHydrated(true)
  }

  const byTab = useMemo(() => {
    const map: Record<string, CosmeticItemDto[]> = {}
    for (const item of data?.catalog ?? []) {
      ;(map[item.slot] ??= []).push(item)
    }
    for (const list of Object.values(map)) list.sort((a, b) => a.sort - b.sort)
    return map
  }, [data])

  if (isLoading || !data) {
    return <div className="max-w-5xl mx-auto p-6 text-ocean-100">Chargement de la cabine…</div>
  }

  const activeTab = TABS.find((t) => t.slot === tab) ?? TABS[0]

  const equip = (item: CosmeticItemDto) => {
    setDirty(true)
    if (activeTab.group === 'avatar') {
      setAvatar((prev) => ({ ...(prev ?? DEFAULT_AVATAR), [item.slot]: item.key }))
    } else if (activeTab.group === 'blason') {
      setBlason((prev) => ({ ...(prev ?? DEFAULT_BLASON), [item.slot]: item.key }))
    } else {
      // Un seul titre : re-cliquer le titre équipé le retire.
      setTitle((prev) => (prev === item.key ? null : item.key))
    }
  }

  const selectedKey =
    activeTab.group === 'avatar'
      ? (avatar as Record<string, string | null | undefined> | null)?.[activeTab.slot]
      : activeTab.group === 'blason'
        ? (blason as Record<string, string | null | undefined> | null)?.[activeTab.slot]
        : title

  const equippedTitleLabel = data.catalog.find((i) => i.key === title)?.label ?? null

  const handleSave = () =>
    save.mutate(
      { avatarConfig: avatar, blasonConfig: blason, equippedTitle: title },
      { onSuccess: () => setDirty(false) },
    )

  /** Vignette « et si ? » : la config courante avec cet item appliqué. */
  const tilePreview = (item: CosmeticItemDto): ReactNode => {
    if (activeTab.group === 'avatar') {
      const cfg = { ...(avatar ?? DEFAULT_AVATAR), [activeTab.slot]: item.key } as AvatarConfigDto
      return <CaptainPortrait config={cfg} size={64} className="mx-auto" />
    }
    if (activeTab.group === 'blason') {
      const cfg = { ...(blason ?? DEFAULT_BLASON), [activeTab.slot]: item.key } as BlasonConfigDto
      return <CaptainBlason config={cfg} size={64} className="mx-auto" />
    }
    return null
  }

  return (
    <div className="max-w-5xl mx-auto p-4 sm:p-6 pb-16">
      <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
        <div>
          <p className="text-[11px] uppercase tracking-[0.32em] text-ocean-300 mb-1">Cabine du capitaine</p>
          <h1 className="font-display text-3xl text-sand-300">Compose ton portrait</h1>
        </div>
        <Link to="/capitaine" className="text-sm text-sand-300 hover:text-sand-100 transition-colors">← Retour au livret de bord</Link>
      </div>

      <div className="grid lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-5 items-start">
        {/* ——— volet aperçu (sticky sur desktop) ——— */}
        <section
          aria-label="Aperçu du capitaine"
          className="rounded-2xl border border-sand-500/20 bg-gradient-to-b from-ocean-700/30 to-[#06222b]/45 p-6 flex flex-col items-center gap-4 lg:sticky lg:top-24 self-start w-full"
        >
          <CaptainPortrait config={avatar} size={200} />
          <div className="text-center">
            <p className="font-display text-2xl text-sand-300">{user?.pseudo}</p>
            {equippedTitleLabel
              ? <p className="font-display text-sm text-sand-100 mt-0.5">{equippedTitleLabel}</p>
              : <p className="text-xs text-ocean-300 mt-0.5">Aucun titre équipé</p>}
          </div>
          <CaptainBlason config={blason} size={96} />
          <button
            type="button"
            disabled={!dirty || save.isPending}
            onClick={handleSave}
            className="mt-2 px-6 py-2.5 rounded-xl bg-sand-500 text-ocean-900 font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sand-300 transition-colors"
          >
            {save.isPending ? 'Sauvegarde…' : 'Sauvegarder'}
          </button>
          {dirty && !save.isPending && (
            <p className="text-[11px] text-ocean-300 -mt-2">Modifications non sauvegardées</p>
          )}
          {save.isError && <p className="text-sm text-rum-500">La sauvegarde a échoué. Réessaie.</p>}
        </section>

        {/* ——— volet items ——— */}
        <section className="min-w-0">
          {/* Bandeau compact sticky mobile : l'aperçu reste sous les yeux pendant
              qu'on scrolle la grille. Rendu seulement sous le breakpoint lg.
              Offsets = hauteur du header sticky : 112px sous `sm` (nav 64
              + mini-bannière ressources 48, `sm:hidden`), 64px au-delà. */}
          {isMobile && (
            <div className="lg:hidden sticky top-[7.5rem] sm:top-[4.25rem] z-20 mb-4 flex items-center gap-3 rounded-xl border border-sand-500/20 bg-[#06222b]/90 backdrop-blur-md px-3 py-2">
              <CaptainPortrait config={avatar} size={56} className="shrink-0" />
              <div className="min-w-0 flex-1">
                <p className="font-display text-base text-sand-300 truncate leading-tight">{user?.pseudo}</p>
                {equippedTitleLabel && <p className="text-[11px] text-sand-100 truncate">{equippedTitleLabel}</p>}
              </div>
              <button
                type="button"
                disabled={!dirty || save.isPending}
                onClick={handleSave}
                className="shrink-0 px-3.5 py-1.5 text-sm rounded-lg bg-sand-500 text-ocean-900 font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-sand-300 transition-colors"
              >
                {save.isPending ? 'Sauvegarde…' : 'Sauvegarder'}
              </button>
            </div>
          )}

          {/* Onglets groupés Avatar / Blason / Titre */}
          <div className="space-y-3 mb-5">
            {TAB_GROUPS.map((group) => (
              <div key={group.id}>
                <p className="text-[10px] uppercase tracking-[0.3em] text-ocean-300 mb-1.5">{group.label}</p>
                <div role="tablist" aria-label={group.label} className="flex flex-wrap gap-1.5">
                  {TABS.filter((t) => t.group === group.id).map((t) => (
                    <button
                      key={t.slot}
                      role="tab"
                      aria-selected={t.slot === tab}
                      onClick={() => setTab(t.slot)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                        t.slot === tab
                          ? 'bg-sand-300/20 border-sand-300/50 text-sand-200'
                          : 'bg-ocean-900/40 border-sand-500/15 text-ocean-300 hover:text-sand-300'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {activeTab.group === 'title' && (
            <p className="text-xs text-ocean-300 mb-3">
              Un seul titre à la fois : re-cliquer le titre équipé le retire.
            </p>
          )}

          <div role="tabpanel" aria-label={activeTab.label} className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {(byTab[activeTab.slot] ?? []).map((item) => {
              const selected = selectedKey === item.key
              return (
                <button
                  key={item.key}
                  type="button"
                  // Gelées pendant la sauvegarde : un équipement en vol suivi du
                  // onSuccess → setDirty(false) marquerait « propre » un brouillon
                  // désynchronisé de ce que le serveur vient d'enregistrer.
                  disabled={!item.owned || save.isPending}
                  aria-pressed={selected}
                  onClick={() => equip(item)}
                  className={`relative rounded-xl border p-3 text-center transition-colors ${
                    selected
                      ? 'border-sand-300/70 bg-sand-300/10'
                      : item.owned
                        ? 'border-sand-500/20 bg-ocean-900/30 hover:border-sand-300/40'
                        : 'border-sand-500/10 bg-ocean-900/20 opacity-60 cursor-not-allowed'
                  }`}
                >
                  {selected && (
                    <span
                      aria-hidden="true"
                      className="absolute top-1.5 right-1.5 grid place-items-center w-5 h-5 rounded-full bg-sand-300 text-ocean-900 text-[11px] font-bold"
                    >
                      ✓
                    </span>
                  )}
                  {activeTab.group === 'title' ? (
                    <span className="grid place-items-center min-h-16 px-1">
                      <span className="font-display text-lg text-sand-200 leading-tight">{item.label}</span>
                    </span>
                  ) : (
                    <>
                      <span aria-hidden="true" className={`block mb-2 ${item.owned ? '' : 'grayscale opacity-70'}`}>
                        {tilePreview(item)}
                      </span>
                      <p className="text-sm text-sand-200 font-medium">{item.label}</p>
                    </>
                  )}
                  {!item.owned && (
                    <p className="text-[11px] text-ocean-300 mt-1">🔒 {item.conditionLabel}</p>
                  )}
                </button>
              )
            })}
          </div>
        </section>
      </div>
    </div>
  )
}
